import tablerIconData from "./tabler-icon-data.generated.json";
import { normalizeTablerIconName, prettifyTablerIconName } from "./tabler-icon-core";

export type TablerIconData = {
  category: string;
  name: string;
  tags: string[];
};

export type TablerIconPickerContext = "default" | "workObjectType";

const data = tablerIconData as TablerIconData[];

const categoryLabelsRu: Record<string, string> = {
  Animals: "Животные",
  Arrows: "Стрелки",
  Badges: "Бейджи",
  Brand: "Бренды",
  Buildings: "Здания",
  Charts: "Графики",
  Communication: "Коммуникация",
  Computers: "Компьютеры",
  Currencies: "Валюты",
  Database: "Данные",
  Design: "Дизайн",
  Development: "Разработка",
  Devices: "Устройства",
  Document: "Документы",
  "E-commerce": "Коммерция",
  Electrical: "Электрика",
  Extensions: "Расширения",
  Food: "Еда",
  Games: "Игры",
  Gender: "Гендер",
  Gestures: "Жесты",
  Health: "Здоровье",
  Laundry: "Стирка",
  Letters: "Буквы",
  Logic: "Логика",
  Map: "Карта",
  Math: "Математика",
  Media: "Медиа",
  Mood: "Настроение",
  Nature: "Природа",
  Numbers: "Числа",
  Photography: "Фото",
  Shapes: "Фигуры",
  Sport: "Спорт",
  Symbols: "Символы",
  System: "Система",
  Text: "Текст",
  Vehicles: "Транспорт",
  "Version control": "Версии",
  Weather: "Погода",
  Zodiac: "Зодиак",
};

const russianSearchAliases: Record<string, string[]> = {
  ai: ["ai", "robot", "brain", "spark"],
  агент: ["ai-agent", "robot", "bot"],
  аналитика: ["chart", "analytics", "stats", "graph", "report"],
  апи: ["api", "webhook", "plug"],
  архив: ["archive", "box", "package"],
  баг: ["bug", "error", "issue"],
  база: ["database", "storage", "data"],
  безопасность: ["shield", "lock", "security", "protect"],
  билд: ["build", "package", "box"],
  блог: ["article", "news", "file-text"],
  бот: ["robot", "bot", "ai"],
  бэклог: ["backlog", "list", "queue", "inbox"],
  вебхук: ["webhook", "link", "api"],
  версия: ["git", "branch", "versions"],
  встреча: ["calendar", "users", "video"],
  гипотеза: ["bulb", "idea", "experiment", "test"],
  график: ["chart", "graph", "timeline"],
  данные: ["data", "database", "table"],
  дедлайн: ["calendar", "clock", "deadline"],
  дизайн: ["design", "palette", "brush"],
  документ: ["file", "document", "text"],
  доставка: ["truck", "rocket", "send"],
  задача: ["task", "todo", "checkbox", "list"],
  заметка: ["note", "file-text", "writing"],
  идея: ["bulb", "idea", "light"],
  интеграция: ["plug", "webhook", "api", "link"],
  исследование: ["search", "eye", "microscope", "discovery"],
  карта: ["map", "route", "location"],
  клиент: ["user", "users", "briefcase", "building-store"],
  код: ["code", "terminal", "brackets"],
  команда: ["users", "team", "people"],
  компонент: ["puzzle", "components", "box"],
  контракт: ["file-certificate", "signature", "handshake"],
  контроль: ["adjustments", "settings", "sliders"],
  метрика: ["chart", "activity", "analytics"],
  ошибка: ["bug", "alert", "error"],
  платеж: ["credit-card", "cash", "currency"],
  план: ["calendar", "map", "timeline", "target"],
  поддержка: ["lifebuoy", "help", "support"],
  пользователь: ["user", "users", "account"],
  приоритет: ["flag", "flame", "alert"],
  проект: ["briefcase", "folder", "rocket"],
  релиз: ["rocket", "package", "tag"],
  риск: ["alert", "shield", "warning"],
  сервис: ["server", "settings", "lifebuoy"],
  статус: ["progress", "circle", "check"],
  сторя: ["book", "story", "file-text"],
  тип: ["category", "tag", "shape"],
  цель: ["target", "goal", "flag"],
  эпик: ["layers", "stack", "blocks"],
};

const contextRecommendations: Record<TablerIconPickerContext, string[]> = {
  default: [
    "circle",
    "star",
    "tag",
    "category",
    "settings",
    "folder",
    "file-text",
    "users",
    "flag",
    "chart-bar",
    "database",
    "bolt",
  ],
  workObjectType: [
    "layers-intersect",
    "checkbox",
    "book-2",
    "bug",
    "bulb",
    "target-arrow",
    "rocket",
    "flag",
    "calendar",
    "briefcase",
    "users",
    "lifebuoy",
    "building-store",
    "heart-handshake",
    "search",
    "eye",
    "atom",
    "brain",
    "chart-bar",
    "database",
    "forms",
    "file-text",
    "code",
    "git-branch",
    "shield-check",
    "bolt",
    "puzzle",
  ],
};

const iconByName = new Map(data.map((icon) => [icon.name, icon]));
const searchableByName = new Map(data.map((icon) => [icon.name, buildSearchText(icon)]));

export const TABLER_ICON_CATEGORIES = Array.from(new Set(data.map((icon) => icon.category))).sort((a, b) =>
  getTablerIconCategoryLabel(a).localeCompare(getTablerIconCategoryLabel(b), "ru"),
);

export const TABLER_ICON_COUNT = data.length;

export function isKnownTablerIconName(icon: string) {
  return iconByName.has(normalizeTablerIconName(icon));
}

export function getTablerIconCategoryLabel(category: string) {
  return categoryLabelsRu[category] ?? category;
}

export function getTablerIconLabel(icon: string) {
  return prettifyTablerIconName(icon);
}

export function getRecommendedTablerIcons(context: TablerIconPickerContext = "default") {
  return contextRecommendations[context].filter(isKnownTablerIconName);
}

export function getTablerIconsByCategory(category: string, limit = 120) {
  return data.filter((icon) => icon.category === category).slice(0, limit).map((icon) => icon.name);
}

export function searchTablerIcons(query: string, options?: { category?: string | null; context?: TablerIconPickerContext; limit?: number }) {
  const normalizedQuery = normalizeSearchText(query);
  const limit = options?.limit ?? 96;

  if (!normalizedQuery && options?.category) {
    return getTablerIconsByCategory(options.category, limit);
  }

  if (!normalizedQuery) {
    return getRecommendedTablerIcons(options?.context).slice(0, limit);
  }

  const queryTokens = expandQueryTokens(normalizedQuery);
  return data
    .filter((icon) => !options?.category || icon.category === options.category)
    .map((icon) => ({ name: icon.name, score: scoreIcon(icon.name, queryTokens) }))
    .filter((match) => match.score > 0)
    .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name))
    .slice(0, limit)
    .map((match) => match.name);
}

function buildSearchText(icon: TablerIconData) {
  const categoryLabel = getTablerIconCategoryLabel(icon.category);
  return normalizeSearchText(`${icon.name} ${prettifyTablerIconName(icon.name)} ${icon.category} ${categoryLabel} ${icon.tags.join(" ")}`);
}

function expandQueryTokens(query: string) {
  const tokens = new Set(query.split(" ").filter(Boolean));
  for (const token of Array.from(tokens)) {
    for (const alias of russianSearchAliases[token] ?? []) {
      normalizeSearchText(alias)
        .split(" ")
        .filter(Boolean)
        .forEach((nextToken) => tokens.add(nextToken));
    }
  }
  return Array.from(tokens);
}

function scoreIcon(iconName: string, queryTokens: string[]) {
  const searchText = searchableByName.get(iconName) ?? "";
  let score = 0;

  for (const token of queryTokens) {
    if (iconName === token) {
      score += 80;
    } else if (iconName.startsWith(token)) {
      score += 45;
    } else if (iconName.includes(token)) {
      score += 25;
    } else if (searchText.includes(token)) {
      score += 10;
    }
  }

  return score;
}

function normalizeSearchText(value: string) {
  return value
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[^a-zа-я0-9]+/g, " ")
    .trim();
}
